import React, { useEffect, useState } from "react";
import { CheckSquare, Eye, FileDown, Loader2, Paperclip, RefreshCw, Search, ShieldAlert, Square, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import DateInput from "../components/DateInput";
import { ExpedientePicker } from "../components/OsintLinks";

const SevBadge = ({ s, t }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${s === "critica" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid={`kev-sev-${s}`}>
    {s === "critica" ? t("cy.critical") : t("cy.high")}
  </span>
);

function Detail({ it, onClose, t }) {
  const rows = [
    [t("cy.cve"), it.cve_id], [t("cy.software"), `${it.vendor} — ${it.product}`], [t("cy.name"), it.name],
    [t("cy.description"), it.description], [t("cy.exploit_date"), it.date_added], [t("cy.action"), it.required_action],
    [t("cy.due"), it.due_date], [t("cy.ransomware"), it.ransomware], ["CWE", (it.cwes || []).join(", ") || "—"],
  ];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="kev-detail-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-extrabold text-slate-900">{it.cve_id}</h3>
            <div className="mt-1"><SevBadge s={it.severity} t={t} /></div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="kev-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-t border-slate-100"><td className="py-2 pr-3 text-xs font-bold uppercase text-slate-500 w-44 align-top">{k}</td><td className="py-2 text-slate-800">{v || "—"}</td></tr>
            ))}
          </tbody>
        </table>
        <a href={it.url} target="_blank" rel="noreferrer" className="text-sm font-bold text-[#044CBC] hover:underline" data-testid="kev-nvd-link">NVD: {it.url}</a>
      </div>
    </div>
  );
}

export default function CyberAgency() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sev, setSev] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState([]);
  const [detail, setDetail] = useState(null);
  const [expId, setExpId] = useState("");
  const [pdfBusy, setPdfBusy] = useState("");
  const [ntas, setNtas] = useState(null);
  const [sups, setSups] = useState(null);
  useEffect(() => {
    api.get("/ntas/alerts").then((r) => setNtas(r.data)).catch(() => setNtas({ active_count: 0 }));
    api.get("/cyber/kev-suppliers").then((r) => setSups(r.data)).catch(() => setSups({ matches: [], suppliers_checked: 0 }));
  }, []);

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ q, date_from: from, date_to: to, severity: sev, limit: 300, ...(refresh ? { refresh: "1" } : {}) });
      const { data } = await api.get(`/cyber/kev?${params}`);
      setData(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const items = data?.items || [];
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOn = items.length > 0 && items.every((i) => sel.includes(i.cve_id));
  const toggleAll = () => setSel(allOn ? sel.filter((x) => !items.some((i) => i.cve_id === x)) : [...new Set([...sel, ...items.map((i) => i.cve_id)])]);
  const filters = { [t("cy.search")]: q, [t("cy.from")]: from, [t("cy.to")]: to, [t("cy.severity")]: sev };

  const pdf = async () => {
    setPdfBusy("pdf");
    try {
      const res = await api.post("/cyber/kev/pdf", { cves: sel, filters }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a"); a.href = url; a.download = "cyber_agency_kev.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setPdfBusy(""); }
  };
  const attach = async () => {
    setPdfBusy("attach");
    try { await api.post("/cyber/kev/attach", { expediente_id: expId, cves: sel }); toast.success(t("osint.attached")); }
    catch (e) { toast.error(apiError(e)); } finally { setPdfBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="cyber-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><ShieldAlert className="w-7 h-7 text-[#044CBC]" /> {t("cy.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("cy.subtitle")}</p>
        </div>
        <button onClick={() => load(true)} disabled={busy} data-testid="kev-refresh-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-bold hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {t("cy.refresh")}
        </button>
      </div>

      {data && (
        <div className="grid md:grid-cols-2 gap-3">
          <a href="/administracion/ntas" className="ec-card px-4 py-3 flex items-center justify-between gap-3 border-l-4 border-l-red-500 hover:bg-slate-50" data-testid="kev-ntas-card">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("cy.ntas_card")}</div>
              <div className="font-heading text-base font-extrabold text-slate-900">{ntas ? (ntas.active_count ? `${ntas.active_count} ${t("nt.active").toLowerCase()}` : t("nt.none_active")) : "…"}</div>
            </div>
            <span className="text-xs font-bold text-[#044CBC]">{t("cy.ntas_open")} →</span>
          </a>
          <div className="ec-card px-4 py-3" data-testid="kev-suppliers-card">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{t("cy.suppliers_title")}</div>
            {!sups ? <div className="text-sm text-slate-400">…</div> : sups.matches.length === 0 ? <div className="text-xs text-slate-500 mt-1">{t("cy.suppliers_none")} ({sups.suppliers_checked} {t("cy.suppliers_checked")})</div> : (
              <ul className="mt-1 space-y-1">
                {sups.matches.map((m) => (
                  <li key={m.supplier_id} className="text-xs flex items-center justify-between gap-2" data-testid={`kev-supplier-${m.supplier_id}`}>
                    <span className="font-semibold text-slate-800 truncate">{m.supplier}</span>
                    <button onClick={() => { setQ(m.matched_terms[0]); setTimeout(() => load(), 0); }} className="text-[#044CBC] font-bold whitespace-nowrap">{m.count} CVE · {m.critical} {t("cy.critical")}</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[[t("cy.catalog_total"), data.total_catalog], [t("cy.critical"), data.critical_count], [t("cy.released"), data.released ? fmtDate(data.released) : "—"], [t("cy.results"), items.length]].map(([k, v]) => (
            <div key={k} className="ec-card px-4 py-3" data-testid={`kev-stat-${k}`}>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{k}</div>
              <div className="font-heading text-lg font-extrabold text-slate-900">{typeof v === "number" ? v.toLocaleString() : v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="ec-card p-4 ec-filter-bar" data-testid="kev-filters">
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="ec-label">{t("cy.search")}</label>
          <input className="ec-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={t("cy.search_ph")} data-testid="kev-q-input" />
        </div>
        <div className="lg:col-span-2">
          <label className="ec-label">{t("cy.severity")}</label>
          <select className="ec-input" value={sev} onChange={(e) => setSev(e.target.value)} data-testid="kev-sev-select">
            <option value="">{t("common.all")}</option><option value="critica">{t("cy.critical")}</option><option value="alta">{t("cy.high")}</option>
          </select>
        </div>
        <div className="lg:col-span-2"><label className="ec-label">{t("cy.from")}</label><DateInput value={from} onChange={(e) => setFrom(e.target.value)} data-testid="kev-from" /></div>
        <div className="lg:col-span-2"><label className="ec-label">{t("cy.to")}</label><DateInput value={to} onChange={(e) => setTo(e.target.value)} data-testid="kev-to" /></div>
        <button onClick={() => load()} disabled={busy} data-testid="kev-search-button" className="ec-filter-btn sm:col-span-2 lg:col-span-1">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {t("common.search")}
        </button>
      </div>

      <div className="ec-card overflow-hidden">
        <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-slate-600" data-testid="kev-selected-count">{sel.length} {t("cy.selected")}</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button onClick={pdf} disabled={!sel.length || !!pdfBusy} data-testid="kev-pdf-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C] disabled:opacity-50">
              {pdfBusy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {t("cy.pdf")}
            </button>
            <ExpedientePicker value={expId} onChange={setExpId} testid="kev-exp-select" />
            <button onClick={attach} disabled={!sel.length || !expId || !!pdfBusy} data-testid="kev-attach-button" className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#16335F] text-[#16335F] text-xs font-bold hover:bg-[#EFF4FC] disabled:opacity-50 whitespace-nowrap">
              {pdfBusy === "attach" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} {t("osint.attach")}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-[#16335F] text-white text-left">
                <th className="px-3 py-2.5"><button onClick={toggleAll} data-testid="kev-select-all" className="text-white">{allOn ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}</button></th>
                {[t("cy.cve"), t("cy.software"), t("cy.name"), t("cy.severity"), t("cy.exploit_date"), t("cy.due"), ""].map((h, i) => <th key={i} className="px-3 py-2.5 text-xs font-bold uppercase tracking-wide">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {!data ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="kev-empty">{t("cy.empty")}</td></tr>
              ) : items.map((it) => (
                <tr key={it.cve_id} className={`border-t border-slate-100 hover:bg-slate-50 ${sel.includes(it.cve_id) ? "bg-[#EFF4FC]" : ""}`} data-testid={`kev-row-${it.cve_id}`}>
                  <td className="px-3 py-2.5"><button onClick={() => toggle(it.cve_id)} data-testid={`kev-check-${it.cve_id}`} className={sel.includes(it.cve_id) ? "text-[#044CBC]" : "text-slate-400"}>{sel.includes(it.cve_id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}</button></td>
                  <td className="px-3 py-2.5 font-mono text-xs font-bold text-slate-800">{it.cve_id}</td>
                  <td className="px-3 py-2.5 text-slate-700">{it.vendor} — {it.product}</td>
                  <td className="px-3 py-2.5 text-slate-600 max-w-xs truncate" title={it.name}>{it.name}</td>
                  <td className="px-3 py-2.5"><SevBadge s={it.severity} t={t} /></td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{it.date_added}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-slate-600">{it.due_date || "—"}</td>
                  <td className="px-3 py-2.5 text-right"><button onClick={() => setDetail(it)} data-testid={`kev-view-${it.cve_id}`} className="text-xs font-bold text-[#033A8C] inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {t("common.view")}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {detail && <Detail it={detail} onClose={() => setDetail(null)} t={t} />}
    </div>
  );
}
